import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  checkoutCart,
  createPayPalOrder,
  capturePayPalOrder,
} from "../actions/cart";

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const {
    items = [],
    status,
    loading,
    checkoutSuccess,
    total_display,
    token,
  } = useSelector((state) => state.cart);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("manual");
  const [deliveryMethod, setDeliveryMethod] = useState("pickup");
  const [shipping, setShipping] = useState({
    shipping_name: "",
    shipping_address1: "",
    shipping_address2: "",
    shipping_city: "",
    shipping_state: "",
    shipping_postcode: "",
    shipping_country: "Australia",
  });
  const [paypalError, setPaypalError] = useState("");
  const [paypalReady, setPaypalReady] = useState(false);
  const paypalContainerRef = React.useRef(null);
  const paypalButtonsRef = React.useRef(null);
  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  useEffect(() => {
    if (paymentMethod !== "paypal" || !paypalClientId) {
      return;
    }
    if (window.paypal) {
      setPaypalReady(true);
      return;
    }
    const existing = document.querySelector("script[data-paypal-script]");
    if (existing) {
      existing.addEventListener("load", () => setPaypalReady(true), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&currency=AUD`;
    script.async = true;
    script.dataset.paypalScript = "true";
    script.onload = () => setPaypalReady(true);
    script.onerror = () =>
      setPaypalError("Unable to load PayPal at the moment.");
    document.body.appendChild(script);
  }, [paymentMethod, paypalClientId]);

  const hasItems = Array.isArray(items) && items.length > 0;
  const hasDigitalItems = items.some((item) => item.product?.is_digital);
  const hasPhysicalItems = items.some((item) => !item.product?.is_digital);
  const isSubmitted = status === "submitted";
  const subtotalLabel = useMemo(() => {
    if (total_display) return total_display;
    return "We will confirm pricing";
  }, [total_display]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!hasItems) {
      setError("Your cart is empty.");
      return;
    }
    if (hasDigitalItems && !form.email.trim()) {
      setError("Enter an email address for your download link.");
      return;
    }
    if (deliveryMethod === "shipping") {
      if (!shipping.shipping_name || !shipping.shipping_address1 || !shipping.shipping_city || !shipping.shipping_state || !shipping.shipping_postcode) {
        setError("Please complete all required shipping fields.");
        return;
      }
      const country = (shipping.shipping_country || "").toLowerCase();
      if (country && !["australia", "au", "aus"].includes(country)) {
        setError("We currently ship within Australia only.");
        return;
      }
    }
    setError("");
    setMessage("");
    const result = await dispatch(
      checkoutCart({
        ...form,
        paymentMethod,
        deliveryMethod: hasPhysicalItems ? deliveryMethod : "digital",
        shipping: hasPhysicalItems && deliveryMethod === "shipping" ? shipping : {},
      })
    );
    if (result.success) {
      setMessage(
        "Thanks! Your order request has been sent. We'll be in touch soon."
      );
      setForm({ name: "", email: "", phone: "", notes: "" });
    } else if (result.error) {
      setError(result.error);
    }
  };

  useEffect(() => {
    if (paymentMethod !== "paypal" || !paypalReady || !hasItems || !token)
      return;
    if (!paypalContainerRef.current || !window.paypal) return;
    if (paypalButtonsRef.current?.close) {
      paypalButtonsRef.current.close();
    }
    setPaypalError("");
    const buttons = window.paypal.Buttons({
      createOrder: async () => {
        const result = await dispatch(createPayPalOrder());
        if (result.success && result.orderId) return result.orderId;
        setPaypalError(result.error || "Unable to start PayPal.");
        throw new Error(result.error || "Unable to start PayPal.");
      },
      onApprove: async (data) => {
        const orderId = data?.orderID;
        const result = await dispatch(capturePayPalOrder(orderId));
        if (result.success) {
          setMessage(
            "Payment completed. Thank you! We will confirm your order details shortly."
          );
          setError("");
          setPaypalError("");
        } else {
          setPaypalError(result.error || "Unable to finalise PayPal payment.");
        }
      },
      onError: (err) => {
        setPaypalError(err?.message || "PayPal encountered an error.");
      },
      onCancel: () => {
        setPaypalError("PayPal checkout was cancelled.");
      },
    });
    if (buttons.isEligible()) {
      buttons.render(paypalContainerRef.current);
      paypalButtonsRef.current = buttons;
    } else {
      setPaypalError(
        "PayPal is not available for this browser or configuration."
      );
    }
    return () => {
      if (paypalButtonsRef.current?.close) {
        paypalButtonsRef.current.close();
      }
      paypalButtonsRef.current = null;
    };
  }, [paymentMethod, paypalReady, hasItems, token, dispatch]);

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Checkout
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Provide your contact details so we can finalise your order and arrange
          payment or invoicing.
        </p>
      </header>

      {!hasItems && !isSubmitted && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-14 text-center text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
          Your cart is empty.{" "}
          <Link
            to="/store"
            className="text-blue-600 hover:underline dark:text-blue-400"
          >
            Return to the store
          </Link>{" "}
          to add items before checking out.
        </div>
      )}

      {(isSubmitted || checkoutSuccess) && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-4 text-sm text-green-800 dark:border-green-500/40 dark:bg-green-900/30 dark:text-green-200">
          Your order request has already been submitted. A member of the team
          will reach out shortly.
        </div>
      )}

      {message && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-500/40 dark:bg-green-900/40 dark:text-green-200">
          {message}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-200"
                  htmlFor="name"
                >
                  Full name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Jane Smith"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-200"
                  htmlFor="email"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-200"
                  htmlFor="phone"
                >
                  Phone number
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Mobile or best contact"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-200"
                  htmlFor="notes"
                >
                  Project notes
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={4}
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Key details, deadlines, delivery address, etc."
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {hasPhysicalItems ? <div className="sm:col-span-2">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Delivery
                </p>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="radio"
                      name="delivery_method"
                      value="pickup"
                      checked={deliveryMethod === "pickup"}
                      onChange={() => setDeliveryMethod("pickup")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    Local pickup
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="radio"
                      name="delivery_method"
                      value="shipping"
                      checked={deliveryMethod === "shipping"}
                      onChange={() => setDeliveryMethod("shipping")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    Ship within Australia
                  </label>
                </div>
              </div> : (
                <div className="sm:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                  Digital delivery — your single-use link will be emailed after payment is confirmed.
                </div>
              )}
              {hasPhysicalItems && deliveryMethod === "shipping" && (
                <div className="sm:col-span-2 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200" htmlFor="shipping_name">
                      Recipient name
                    </label>
                    <input
                      id="shipping_name"
                      name="shipping_name"
                      type="text"
                      value={shipping.shipping_name}
                      onChange={(e) => setShipping((prev) => ({ ...prev, shipping_name: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200" htmlFor="shipping_address1">
                      Street address
                    </label>
                    <input
                      id="shipping_address1"
                      name="shipping_address1"
                      type="text"
                      value={shipping.shipping_address1}
                      onChange={(e) => setShipping((prev) => ({ ...prev, shipping_address1: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200" htmlFor="shipping_address2">
                      Address line 2 (optional)
                    </label>
                    <input
                      id="shipping_address2"
                      name="shipping_address2"
                      type="text"
                      value={shipping.shipping_address2}
                      onChange={(e) => setShipping((prev) => ({ ...prev, shipping_address2: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200" htmlFor="shipping_city">
                      City / Suburb
                    </label>
                    <input
                      id="shipping_city"
                      name="shipping_city"
                      type="text"
                      value={shipping.shipping_city}
                      onChange={(e) => setShipping((prev) => ({ ...prev, shipping_city: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200" htmlFor="shipping_state">
                      State / Territory
                    </label>
                    <input
                      id="shipping_state"
                      name="shipping_state"
                      type="text"
                      value={shipping.shipping_state}
                      onChange={(e) => setShipping((prev) => ({ ...prev, shipping_state: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200" htmlFor="shipping_postcode">
                      Postcode
                    </label>
                    <input
                      id="shipping_postcode"
                      name="shipping_postcode"
                      type="text"
                      value={shipping.shipping_postcode}
                      onChange={(e) => setShipping((prev) => ({ ...prev, shipping_postcode: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200" htmlFor="shipping_country">
                      Country (AU only)
                    </label>
                    <input
                      id="shipping_country"
                      name="shipping_country"
                      type="text"
                      value={shipping.shipping_country}
                      onChange={(e) => setShipping((prev) => ({ ...prev, shipping_country: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      required
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                Payment method
              </p>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="radio"
                  name="payment_method"
                  value="manual"
                  checked={paymentMethod === "manual"}
                  onChange={() => setPaymentMethod("manual")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                Cash on pickup / Invoice (we'll follow up)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="radio"
                  name="payment_method"
                  value="paypal"
                  checked={paymentMethod === "paypal"}
                  onChange={() => setPaymentMethod("paypal")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  disabled={!paypalClientId}
                />
                PayPal {paypalClientId ? "" : "(configure client ID to enable)"}
              </label>
            </div>
            <button
              type="submit"
              disabled={!hasItems || isSubmitted || loading}
              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition ${
                !hasItems || isSubmitted
                  ? "cursor-not-allowed bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {loading ? "Submitting..." : "Submit order request"}
            </button>
          </form>
        </section>

        <aside className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Order summary
          </h2>
          {hasItems ? (
            <ul className="space-y-3 text-sm text-gray-700 dark:text-gray-200">
              {items.map((item) => {
                const label = item.display_name || item.product?.name || "Item";
                const detail =
                  item.print_request?.filename ||
                  item.product?.description ||
                  "";
                const showShipping = deliveryMethod === "shipping" && items[0] === item;
                return (
                  <li key={item.id} className="flex justify-between gap-4">
                    <span className="flex-1">
                      {label}
                      {item.quantity > 1 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {" "}
                          × {item.quantity}
                        </span>
                      )}
                      {detail ? (
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {detail}
                        </span>
                      ) : null}
                      {showShipping && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          Ship to: {shipping.shipping_address1}
                          {shipping.shipping_city ? `, ${shipping.shipping_city}` : ""}{" "}
                          {shipping.shipping_state} {shipping.shipping_postcode}
                        </span>
                      )}
                    </span>
                    <span className="text-right">
                      {item.line_total_display ||
                        item.unit_price_display ||
                        "Custom"}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Add items to your cart to see a summary here.
            </p>
          )}

          <div className="mt-6 border-t border-dashed border-gray-200 pt-4 text-sm dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-800 dark:text-gray-100">
                Estimated total
              </span>
              <span className="text-gray-900 dark:text-gray-100">
                {subtotalLabel}
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Final pricing is confirmed once we review your order and
              materials. You will receive a confirmation email.
            </p>
          </div>
          {paymentMethod === "paypal" && paypalClientId ? (
            <div className="mt-6">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
                Pay with PayPal
              </p>
              <div ref={paypalContainerRef} className="paypal-buttons" />
              {paypalError ? (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                  {paypalError}
                </p>
              ) : (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  You can still submit the form for invoicing if you prefer.
                </p>
              )}
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
};

export default CheckoutPage;
